import {
	createContext,
	useContext,
	useEffect,
	useState,
	useSyncExternalStore,
} from "react"

// Utility to detect mobile devices
const isMobileDevice = () => {
	if (typeof navigator === "undefined") return false
	return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

// Default initial size (safe for SSR)
const defaultSize = {
	width: 0,
	height: 0,
	isMobile: false,
	isTablet: false,
	isDesktop: false,
	isLandscape: false,
}

// Calculate window size state
const calculateSize = () => {
	if (typeof window === "undefined") return defaultSize
	const width = window.innerWidth
	const height = window.innerHeight
	return {
		width,
		height,
		isMobile: width < 768,
		isTablet: width >= 768 && width < 1024,
		isDesktop: width >= 1024,
		isLandscape: !isMobileDevice() || width > height,
	}
}

// --- Store using SyncExternalStore ---
let currentSize = calculateSize()
const listeners = new Set()

const subscribe = (callback) => {
	listeners.add(callback)
	return () => listeners.delete(callback)
}

const emitChange = () => {
	currentSize = calculateSize()
	listeners.forEach((cb) => cb())
}

// Start listening to resize events on the client
if (typeof window !== "undefined") {
	window.addEventListener("resize", emitChange)
}

// --- Hook with selector ---
export const useWindowSizeValue = (selector) =>
	useSyncExternalStore(
		subscribe,
		() => selector(currentSize),
		() => selector(defaultSize)
	)

// --- Convenience hooks ---
export const useWindowSize = () => useWindowSizeValue((s) => s)
export const useIsMobile = () => useWindowSizeValue((s) => s.isMobile)
export const useIsTablet = () => useWindowSizeValue((s) => s.isTablet)
export const useIsDesktop = () => useWindowSizeValue((s) => s.isDesktop)
export const useIsLandscape = () => useWindowSizeValue((s) => s.isLandscape)
